/**
 * Who does the 8D (spec A5, 24 Sep 2026): the customer's own team on an
 * internal, supplier-less (quality.no_supplier) or customer-liability NCR,
 * the supplier otherwise. The frontend's
 * one copy of the backend's `customer_owns_8d` (quality/services/ncr_lifecycle.py).
 */
import type { NonConformanceReport } from '../../lib/api';

type OwnershipFields = Pick<NonConformanceReport, 'is_internal' | 'liability_type'>
  & Partial<Pick<NonConformanceReport, 'supplier'>>;

/**
 * Whether the customer's team, not the supplier, writes this NCR's RCA and CAR.
 *
 * @param ncr - the NCR, or null/undefined while it loads
 * @returns true for an internal, supplier-less or customer-liability NCR
 */
export function customerOwns8D(ncr: OwnershipFields | null | undefined): boolean {
  // `supplier === null` only: a caller that never loaded the field (undefined)
  // keeps the old rule rather than reading as supplier-less.
  return !!ncr?.is_internal || ncr?.supplier === null || ncr?.liability_type === 2;
}

/**
 * The owner named in the 8D card titles, e.g. "Root Cause Analysis (Customer)".
 *
 * @param ncr - the NCR
 * @returns 'Internal', 'Customer' or 'Supplier Only'
 */
export function eightDOwnerLabel(ncr: OwnershipFields): 'Internal' | 'Customer' | 'Supplier Only' {
  if (ncr.is_internal || ncr.supplier === null) return 'Internal';
  return customerOwns8D(ncr) ? 'Customer' : 'Supplier Only';
}
